// PRFAQSection.js - New Zealand
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';

const PRFAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'How many points do I need for New Zealand PR?',
      answer: 'You need a minimum of 160 points to submit an Expression of Interest (EOI) for the Skilled Migrant Category. However, competitive selection typically occurs at 180+ points. Points are awarded for age (20-39 = 30 points), qualifications (Bachelor = 40, Master = 50, PhD = 70), skilled employment in NZ (50-60 points), work experience, and partner qualifications.'
    },
    {
      question: 'Do I need a job offer for New Zealand PR?',
      answer: 'For the Skilled Migrant Category, having a job offer in New Zealand significantly increases your chances of selection and adds 50-60 points to your score. While technically possible to apply without a job offer, most successful applicants have NZ employment. Work to Residence pathways require a job offer from an accredited employer.'
    },
    {
      question: 'What English test scores are required?',
      answer: 'The minimum requirement is IELTS 6.5 overall with no band less than 6.0 (or equivalent: TOEFL iBT 79, PTE Academic 58, OET Grade B). Test results must be less than 2 years old at the time of application. Higher English scores do not provide additional points but meet the requirement.'
    },
    {
      question: 'How long does the New Zealand PR process take?',
      answer: 'For Skilled Migrant Category, the total timeline is typically 8-18 months from EOI submission to residence grant. This includes waiting for EOI selection (varies), 4 months to lodge application after invitation, and 6-12 months processing time. Work to Residence takes 30-40 months total including the work visa period.'
    },
    {
      question: 'Can I include my family in my PR application?',
      answer: 'Yes, you can include your partner and dependent children (under 24, single, and financially dependent) in your residence application. Including your partner can add up to 20 points if they have qualifications and/or skilled employment. Each additional applicant requires additional government fees.'
    },
    {
      question: 'What is an accredited employer?',
      answer: 'An accredited employer is a New Zealand employer approved by Immigration NZ to hire migrant workers. Accreditation verifies the employer is genuine, compliant with employment laws, and committed to good employment practices. Job offers from accredited employers are required for Work to Residence pathways.'
    },
    {
      question: 'What occupations are in demand in New Zealand?',
      answer: 'New Zealand has ongoing demand for healthcare professionals (nurses, doctors), IT professionals (software developers, analysts), engineers (civil, mechanical, electrical), construction trades (electricians, plumbers, carpenters), and teachers. The Long Term Skill Shortage List (LTSSL) identifies occupations with sustained shortages.'
    },
    {
      question: 'What happens after I get PR?',
      answer: 'After receiving your residence visa, you can live, work, and study anywhere in New Zealand indefinitely. You have access to public healthcare and education. You can travel in and out of NZ for up to 2 years on your resident visa. After 5 years of residence, you can apply for New Zealand citizenship. NZ allows dual citizenship.'
    },
    {
      question: 'Is there an age limit for New Zealand PR?',
      answer: 'For the Skilled Migrant Category, you must be under 56 years old at the time of application. Maximum points (30) are awarded for ages 20-39. Ages 40-44 receive 20 points, 45-49 receive 10 points, and 50-55 receive 5 points. There is no age limit for Work to Residence or Partnership categories.'
    },
    {
      question: 'What is the success rate for New Zealand PR applications?',
      answer: 'Success rates are high for applications meeting all requirements. Skilled Migrant Category applications with 180+ points and complete documentation typically have 85-90% approval rates. Our clients who follow our guidance and submit well-prepared applications have a 98% success rate. The key is ensuring eligibility, maximizing points, and providing complete documentation.'
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-600 text-base font-light">
            Common questions about New Zealand permanent residency applications
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className="bg-[#F5F8FA] rounded-lg shadow-sm overflow-hidden border border-gray-200"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
              >
                <span className="text-base font-semibold text-[#202356] pr-4">
                  {faq.question}
                </span>
                <div className="flex-shrink-0">
                  {openIndex === index ? (
                    <Minus size={24} className="text-[#2542D4]" />
                  ) : (
                    <Plus size={24} className="text-[#2542D4]" />
                  )}
                </div>
              </button>

              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 text-gray-600 text-sm font-light leading-relaxed border-t border-gray-200 pt-4">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6 text-center"
        >
          <p className="text-gray-700 text-sm font-light leading-relaxed">
            <strong>Still have questions?</strong> Book a free consultation with our immigration experts to discuss your specific situation and get personalized guidance on your New Zealand PR journey.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default PRFAQSection;
